<template>
  <div class="container v-content-container popup-wrapper">
    <div class="row">
      <div class="col-xs-12">
        <h1>
          <i class="fa fa-fw fa-lg fa-list-alt"></i>
          <span v-if="selectedEventAppointmentId != null">
            {{ $label("event.event_appointment", "Event appointment") }}
          </span>
          <span v-else-if="!loading">
            {{ $label("event.new_event_appointment", "New event appointment") }}
          </span>
        </h1>
      </div>
    </div>

    <navigation-tabs :event-id="selectedEventId"></navigation-tabs>

    <div class="row">
      <div class="col-xs-12">&nbsp;</div>
    </div>

    <error404 v-if="noDataExist" />

    <template v-else>
      <div v-show="loading" class="row">
        <div class="col-xs-12">
          <view-loader
            margin-top-pixels="15"
            border-color="primary"
          ></view-loader>
        </div>
      </div>

      <div v-show="!loading" id="event-appointment-form">
        <section>
          <div class="row">
            <div class="col-sm-3 col-xs-12">
              <h2>
                <i class="fa fa-fw fa-lg fa-clock-o"></i>
                {{ $label("event.event_appointment", "Appointment") }}
              </h2>
            </div>
            <div class="col-sm-6 col-xs-12 mt-6">
              <div class="v-form-partial">
                <div class="v-form">
                  <fieldset>
                    <div class="row">
                      <!-- BEGIN Appointment date -->
                      <section class="col col-6">
                        <label class="label">{{
                          $label("event.date", "Date")
                        }}</label>
                        <date-picker
                          v-model="date"
                          :format="dateFormat"
                          :disabled="updating"
                          :readonly="updating"
                          :placeholder="dateFormat"
                          valueType="format"
                          type="date"
                          class="input"
                          id="appointment-date"
                        >
                        </date-picker>
                      </section>
                      <!-- END Appointment date -->
                      <!-- BEGIN Appointment venue -->
                      <section class="col col-6">
                        <label class="label">{{
                          $label("event.venue", "Venue")
                        }}</label>

                        <label class="select">
                          <select
                            class="text-capitalize"
                            id="appointment-venue"
                            :disabled="updating"
                            v-model="selectedEventAppointment.venue_id"
                          >
                            <option
                              v-for="venue in venues"
                              :key="venue.id"
                              :value="venue.id"
                              class="text-capitalize"
                              >{{ venue.name }}</option
                            >
                          </select>
                          <i></i>
                        </label>

                        <p v-if="hasError('venue_id')" class="text-danger">
                          {{ getError("venue_id") }}
                        </p>
                      </section>
                      <!-- END Appointment venue -->
                    </div>
                    <div class="row">
                      <!-- BEGIN Appointment starts time -->
                      <section class="col col-6">
                        <label class="label">{{
                          $label("event.starts_at", "Starts at")
                        }}</label>
                        <date-picker
                          v-model="startTime"
                          :format="timeFormat"
                          :readonly="updating"
                          :disabled="updating"
                          :placeholder="timeFormat"
                          valueType="format"
                          type="time"
                          class="input"
                          id="appointment-starts-at"
                          :class="{
                            'text-input-error': hasError('starts_at')
                          }"
                        >
                        </date-picker>

                        <p v-if="hasError('starts_at')" class="text-danger">
                          {{ getError("starts_at") }}
                        </p>
                      </section>
                      <!-- END Appointment starts time -->
                      <!-- BEGIN Appointment ends time -->
                      <section class="col col-6">
                        <label class="label">{{
                          $label("event.ends_at", "Ends at")
                        }}</label>
                        <date-picker
                          v-model="endTime"
                          :format="timeFormat"
                          :readonly="updating"
                          :disabled="updating"
                          :placeholder="timeFormat"
                          valueType="format"
                          type="time"
                          class="input"
                          id="appointment-ends-at"
                          :class="{
                            'text-input-error': hasError('ends_at')
                          }"
                        >
                        </date-picker>

                        <p v-if="hasError('ends_at')" class="text-danger">
                          {{ getError("ends_at") }}
                        </p>
                      </section>
                      <!-- END Appointment ends time -->
                    </div>
                  </fieldset>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>

      <div class="row mb-4">
        <div class="col-xs-12">&nbsp;</div>
      </div>
      <hr />

      <div class="row">
        <div class="col-xs-12 text-center">
          <back-button
            back-link="EventAppointments"
            :class="{ 'is-disabled': updating }"
            id="back-event-date-settings-button"
            icon="fa fa-lg fa-fw fa-arrow-circle-o-left"
            :text="$label('common.back', 'Back')"
          >
          </back-button>
          &nbsp;
          <info-button
            :class="{
              'is-disabled': updating || selectedEventAppointmentId == null
            }"
            id="save-appointment-button"
            icon="fa fa-lg fa-fw fa-save"
            :text="$label('common.save', 'Save')"
            @click.native="createOrUpdateAppointment"
          >
          </info-button>
          &nbsp;
          <danger-button
            :class="{
              'is-disabled': updating || selectedEventAppointmentId == null
            }"
            id="delete-appointment-button"
            icon="fa fa-fw fa-lg fa-trash-o"
            :text="$label('common.delete', 'Delete')"
            @click.native="deleteEventAppointment"
          >
          </danger-button>
        </div>
        <div class="row mb-4">
          <div class="col-xs-12">&nbsp;</div>
        </div>
      </div>
    </template>
  </div>
</template>

<script>
import Api from "@/api.js";
import FormErrorsMixin from "@/mixins/FormErrors";
import DatePicker from "vue2-datepicker";
import "vue2-datepicker/index.css";
import "vue2-datepicker/locale/de";

import NavigationTabs from "@/components/Settings/Events/EventDetails/NavigationTabs.vue";
import ViewLoader from "@/components/Common/ViewLoader";
import InfoButton from "@/components/Common/Buttons/InfoButton";
import BackButton from "@/components/Common/Buttons/BackButton";
import DangerButton from "@/components/Common/Buttons/DangerButton";
import Error404 from "@/components/Common/Error404";
import swal from "sweetalert";

export default {
  name: "EventAppointmentDetails.vue",

  mixins: [FormErrorsMixin],

  components: {
    ViewLoader,
    NavigationTabs,
    InfoButton,
    BackButton,
    DangerButton,
    DatePicker,
    Error404
  },

  data() {
    return {
      loading: false,
      updating: false,
      noDataExist: false,
      dateFormat: "DD.MM.YYYY",
      timeFormat: "HH:mm",
      date: "2020-01-01",
      startTime: null,
      endTime: null,
      venues: [],
      selectedEvent: {
        name: null
      },
      selectedEventId: null,
      selectedEventAppointmentId: null,
      selectedEventAppointment: {
        starts_at: null,
        ends_at: null,
        venue_id: null
      },
      validatedInputs: ["starts_at", "ends_at", "venue_id"]
    };
  },

  beforeRouteEnter(to, from, next) {
    next(vm => {
      vm.selectedEventId = vm.$route.params.event_id;
      vm.selectedEventAppointmentId = vm.$route.params.appointment_id;

      // Get Event
      if (
        typeof vm.selectedEventId !== "undefined" &&
        vm.selectedEventId !== null
      ) {
        vm.getEvent(() => {
          //
        });
      }

      // Get Event Appointment
      if (
        typeof vm.selectedEventAppointmentId !== "undefined" &&
        vm.selectedEventAppointmentId !== null
      ) {
        if (vm.$route.query.event_appointment_created) {
          swal("Success!", "The appointment was created.", "success").then(
            () => {
              window.history.pushState(
                {},
                document.title,
                window.location.pathname
              );
            }
          );
        }
        vm.getEventAppointment(() => {
          //
        });
      }

      // Get Venues
      vm.getVenues(() => {
        //
      });
    });
  },

  beforeRouteUpdate(to, from, next) {
    // Get Event
    if (
      typeof this.selectedEventId !== "undefined" &&
      this.selectedEventId !== null
    ) {
      this.getEvent(() => {
        //
      });
    }

    // Get Event Appointment
    if (
      typeof this.selectedEventAppointmentId !== "undefined" &&
      this.selectedEventAppointmentId !== null
    ) {
      this.getEventAppointment(() => {
        //
      });
    }

    // Get Venues
    this.getVenues(() => {
      //
    });
    next();
  },

  mounted() {
    //
  },

  methods: {
    createOrUpdateAppointment() {
      if (this.selectedEventAppointmentId == null) {
        this.createEventAppointment();
      } else {
        this.updateEventAppointment();
      }
    },

    getEvent() {
      this.resetErrors();
      this.loading = true;
      const endpoint = this.$partnerEndpoint("events/" + this.selectedEventId);

      Api()
        .get(endpoint)
        .then(response => {
          this.selectedEvent = response.data.data;
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          console.log(error.response);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    getEventAppointment() {
      this.resetErrors();
      this.loading = true;
      const endpoint = this.$partnerEndpoint(
        "events/" +
          this.selectedEventId +
          "/appointments/" +
          this.selectedEventAppointmentId
      );

      Api()
        .get(endpoint)
        .then(response => {
          if (response.data.data.venue && response.data.data.venue.id) {
            this.selectedEventAppointment.venue_id =
              response.data.data.venue.id;
          }
          if (response.data.data.starts_at && response.data.data.ends_at) {
            this.date = this.$moment(response.data.data.starts_at).format(
              this.dateFormat
            );
            this.startTime = this.$moment(response.data.data.starts_at).format(
              this.timeFormat
            );
            this.endTime = this.$moment(response.data.data.ends_at).format(
              this.timeFormat
            );
          }
        })
        .catch(error => {
          if (error.response.status === 404) {
            this.noDataExist = true;
          }
          console.log(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },

    updateEventAppointment() {
      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint(
        "events/" +
          this.selectedEventId +
          "/appointments/" +
          this.selectedEventAppointmentId
      );

      Api()
        .put(endpoint, this.selectedEventAppointment)
        .then(response => {
          this.selectedEventAppointmentId = response.data.data.id;
          this.getEventAppointment();
        })
        .catch(error => {
          console.log(error);
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    createEventAppointment() {
      if (this.updating) {
        return;
      }

      this.resetErrors();
      this.updating = true;
      const endpoint = this.$partnerEndpoint(
        "events/" + this.selectedEventId + "/appointments"
      );

      Api()
        .post(endpoint, this.selectedEventAppointment)
        .then(response => {
          this.selectedEventAppointmentId = response.data.data.id;
          this.$router.push({
            name: "EventAppointmentDetails",
            params: {
              event_id: this.selectedEventId,
              appointment_id: this.selectedEventAppointmentId
            },
            query: { event_appointment_created: true }
          });
        })
        .catch(error => {
          this.storeErrors(error.response.data.errors);
        })
        .finally(() => {
          this.updating = false;
        });
    },

    deleteEventAppointment() {
      if (this.loading || this.updating) {
        return;
      }

      swal({
        title: "Are you sure?",
        text: "The appointment will be deleted and will not be able to use it",
        icon: "warning",
        dangerMode: true,
        buttons: {
          cancel: {
            visible: true,
            text: "Cancel",
            closeModal: true
          },
          confirm: {
            visible: true,
            text: "Yes, delete it!",
            closeModal: true
          }
        }
      }).then(isConfirm => {
        if (isConfirm) {
          const endpoint = this.$partnerEndpoint(
            "events/" +
              this.selectedEventId +
              "/appointments/" +
              this.selectedEventAppointmentId
          );

          Api()
            .delete(endpoint)
            .then(response => {
              console.log(response.status);
              this.$router.push({
                name: "EventAppointments",
                query: { event_appointment_deleted: true }
              });
            })
            .catch(error => {
              console.log(error.response);
              if (error.response.status === 403) {
                swal({
                  title: "Missing permission",
                  text: "You are not allowed to delete event appointments.",
                  icon: "error"
                  //dangerMode: true,
                });
              }
            });
        } else {
          //
        }
      });
    },

    getVenues(successCallback) {
      this.loading = true;
      this.venues = [];
      const endpoint = this.$partnerEndpoint("venues");

      Api()
        .get(endpoint, {
          params: {
            page: 1,
            search: ""
          }
        })
        .then(response => {
          this.venues = response.data.data;
          if (
            this.selectedEventAppointment.venue_id === null &&
            response.data.data.length
          ) {
            this.selectedEventAppointment.venue_id = response.data.data[0].id;
          }
          this.failedRequest = false;
          if (typeof successCallback == "function") {
            successCallback();
          }
        })
        .catch(() => {
          this.failedRequest = true;
        })
        .finally(() => {
          this.loading = false;
        });
    }
  },
  watch: {
    date(state) {
      this.selectedEventAppointment.starts_at = `${this.convertDate(state)} ${
        this.startTime
      }:00`;
      this.selectedEventAppointment.ends_at = `${this.convertDate(state)} ${
        this.endTime
      }:00`;
    },
    startTime(state) {
      this.selectedEventAppointment.starts_at = `${this.convertDate(
        this.date
      )} ${state}:00`;
    },
    endTime(state) {
      this.selectedEventAppointment.ends_at = `${this.convertDate(
        this.date
      )} ${state}:00`;
    }
  }
};
</script>

<style scoped>
.mb-4 {
  margin-bottom: 1.2rem;
}

.mt-6 {
  margin-top: 1.8rem;
}
</style>
